import { getAnalyticsMetaProperties } from './meta-properties';
import { sanitizedLocation } from './sanitized-location';
import { Events, type IEvents } from './types';

/** The wire name of a catalogued event, or `null` when the catalog has no such entry. */
export function getEventName<Namespace extends keyof IEvents>(
  namespace: Namespace,
  event: keyof IEvents[Namespace] & string,
): string | null {
  const eventDetails: unknown = Events[namespace]?.[event];
  if (
    typeof eventDetails === 'object' &&
    eventDetails !== null &&
    'name' in eventDetails &&
    typeof eventDetails.name === 'string'
  ) {
    return eventDetails.name;
  }
  return null;
}

export type TrackEventBody<Properties extends object> = {
  event: string;
  properties: ReturnType<typeof getAnalyticsMetaProperties> & Properties;
};

/**
 * The request body every tracking endpoint takes: the catalogued wire name
 * plus the standard meta block under the per-event properties. `null` for an
 * event the catalog does not know, which callers skip rather than post.
 */
export function buildTrackEventBody<Namespace extends keyof IEvents, Properties extends object>(
  namespace: Namespace,
  event: keyof IEvents[Namespace] & string,
  properties: Properties,
): TrackEventBody<Properties> | null {
  const eventName = getEventName(namespace, event);
  if (eventName == null) {
    return null;
  }
  return { event: eventName, properties: { ...getAnalyticsMetaProperties(), ...sanitizedLocation(), ...properties } };
}

/**
 * Post a catalogued event through the given endpoint. Resolves on success and
 * failure alike — analytics must never block or surface in the app — and skips
 * the post entirely for an event the catalog does not know.
 */
export function postCatalogEvent<Namespace extends keyof IEvents, Properties extends object, Response>(
  namespace: Namespace,
  event: keyof IEvents[Namespace] & string,
  properties: Properties,
  post: (body: TrackEventBody<Properties>) => Promise<Response>,
): Promise<void> {
  const body = buildTrackEventBody(namespace, event, properties);
  if (body == null) {
    return Promise.resolve();
  }
  return post(body).then(
    () => undefined,
    () => undefined,
  );
}
